import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { SA_SIBLINGS } from "@/data/sanAntonioSiblings";
import hero from "@/assets/original/stock-traditional.jpg";
import heroPicture from "@/assets/original/stock-traditional.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";

const PATH = "/patio-covers/in/san-antonio";
const TITLE = "Patio Covers in San Antonio, TX | Texas Made Shade";
const DESC = "Custom patio covers in San Antonio, TX — welded aluminum frames, Sunbrella, Phifertex Plus and insulated tops. Built in Seguin, installed across Bexar County. Free on-site quotes.";

export const Route = createFileRoute("/patio-covers/in/san-antonio")({
  head: (ctx) => ({
    links: [canonicalLink(PATH)],
    meta: routeMeta(ctx, {
      title: TITLE,
      description: DESC,
      image: hero,
      imageAlt: "Custom welded patio cover installed at a San Antonio, TX backyard",
      socialTitle: "Patio Covers in San Antonio, TX",
      socialDescription: DESC,
    }),
    scripts: [
      ...serviceHeadScripts({
        path: PATH,
        serviceName: "Patio Covers in San Antonio, TX",
        breadcrumbName: "Patio Covers in San Antonio",
        description: DESC,
        faqs: [
          { q: "How much does a patio cover cost in San Antonio?", a: "Most residential aluminum patio covers in San Antonio fall between $4,500 and $9,500 depending on span, fabric vs solid top, and attachment to the home. Every quote is written, itemized and free." },
          { q: "Do I need a permit for a patio cover in San Antonio?", a: "Inside San Antonio city limits attached patio covers usually require a residential permit and an HOA review. We pull permits and provide stamped drawings on request; outside city limits in Bexar County most freestanding covers do not." },
          { q: "How long does it take to build a patio cover in San Antonio?", a: "From signed quote to installed cover most San Antonio jobs run 3–5 weeks. Fabrication happens in our Seguin shop so the on-site install is typically a single day." },
          { q: "What kind of warranty comes with your patio covers?", a: "Welded aluminum frames carry a 20-year structural warranty. Sunbrella canvas tops carry a 10-year Sunbrella warranty plus our 2-year workmanship warranty." },
        ],
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Patio Covers in San Antonio, TX",
          serviceType: "Patio Cover Installation",
          url: canonicalUrl(PATH),
          provider: { "@id": "https://texasmadeshade.com/#business" },
          areaServed: { "@type": "City", name: "San Antonio", containedInPlace: { "@type": "AdministrativeArea", name: "Bexar County" } },
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <ServicePage
      currentPath={PATH}
      serviceName="Patio Covers in San Antonio"
      breadcrumbs={[
        { label: "Patio & Pergola Covers", to: "/pergola-covers" },
        { label: "Patio Covers in San Antonio, TX" },
      ]}
      eyebrow="★ PATIO COVERS · SAN ANTONIO, TX ★"
      title="Custom Patio Covers in San Antonio, TX"
      intro="From Stone Oak to Alamo Ranch, we design, weld and install custom patio covers that survive San Antonio summers — attached covers, freestanding pavilions, outdoor-kitchen shade and full pergola roofs. Built in our Seguin shop, installed by the same crew."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Patio Cover Builder · Serving San Antonio, TX",
        headline: "San Antonio Patio Cover Quote — Free On-Site Estimate",
        subhead: "Send a few photos of your San Antonio backyard and rough dimensions. We'll come back same business day with a written estimate.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Patio / Pergola Cover")}&notes=${encodeURIComponent("Patio cover quote — project in San Antonio, TX.")}`,
        primaryLabel: "Get My San Antonio Patio Cover Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Welded aluminum frames — no rust, no rot",
        "Attached, freestanding, gable & flat designs",
        "Sunbrella, Phifertex Plus & insulated tops",
        "Engineered for sustained 60+ mph wind",
        "Permits & HOA drawings for San Antonio jobs",
        "Serving all of Bexar County & the Hill Country",
      ]}
      body={[
        {
          heading: "Why San Antonio Patios Need More Than a Wood Pergola",
          text: "Cedar and pine pergolas split, sag and turn grey inside two summers under South Texas sun. Our powder-coated aluminum frames don't splinter, don't host termites and don't need staining every spring. The Sunbrella tops we sew in Seguin hold their color for 8–12 years even in full Bexar County sun.",
        },
        {
          heading: "Designs Built for San Antonio Homes",
          text: "We build attached covers that tie cleanly into stucco, stone and Hardie siding common in San Antonio neighborhoods like Alamo Heights, Helotes and the Dominion. Freestanding pavilions sit over pools, hot tubs and outdoor kitchens without touching the house — useful when HOA setbacks are tight.",
          cta: { label: "See our pergola cover options", href: "/pergola-covers" },
        },
        {
          heading: "Outdoor Kitchens, Pools & Grill Islands",
          text: "Putting in a built-in grill or smoker? We size shade to keep the cook out of direct sun without trapping heat under the cover — usually a vented gable with a Phifertex Plus top or an insulated Snap-N-Lock panel when full rain protection matters.",
          cta: { label: "Pool & spa shade", href: "/pool-spa" },
        },
        {
          heading: "Permits, HOA & Engineering",
          text: "Inside San Antonio city limits we pull the residential permit and provide stamped wind-load drawings when your HOA asks. Outside city limits in Bexar County most freestanding pavilions don't need a permit — we'll tell you up front either way.",
        },
      ]}
      applications={[
        "Backyard Patio Covers",
        "Outdoor Kitchen Shade",
        "Pool Cabanas",
        "Front Porch Covers",
        "Carport & Side-Yard Covers",
        "Drop Curtains & Wind Walls",
        "Replacement Pergola Tops",
      ]}
      faqs={[
        { q: "How much does a patio cover cost in San Antonio?", a: "Most residential aluminum patio covers in San Antonio fall between $4,500 and $9,500 depending on span, fabric vs solid top, and attachment to the home. Every quote is written, itemized and free." },
        { q: "Do I need a permit for a patio cover in San Antonio?", a: "Inside city limits attached patio covers usually require a residential permit and HOA review. We pull permits and provide stamped drawings on request; outside city limits most freestanding covers do not." },
        { q: "How long does install take?", a: "From signed quote to installed cover most San Antonio jobs run 3–5 weeks. Fabrication happens in our Seguin shop so the on-site install is typically a single day." },
        { q: "Aluminum vs. wood pergola — which holds up in San Antonio?", a: "Aluminum every time. Wood splinters, sags and needs staining every 2–3 years. Welded aluminum is powder-coated, never rusts and lasts 20+ years with zero maintenance." },
      ]}
      cityCallouts={[
        { to: "/san-antonio", city: "San Antonio", anchorText: "All Texas Made Shade services in San Antonio, TX", blurb: "See every awning, cover, sail-shade and welding service we offer to San Antonio." },
        { to: "/pergola-covers/in/new-braunfels", city: "New Braunfels", anchorText: "Patio covers in New Braunfels, TX", blurb: "We also build custom patio and pergola covers in nearby New Braunfels (Comal County)." },
        { to: "/pergola-covers/in/boerne", city: "Boerne", anchorText: "Patio covers in Boerne, TX", blurb: "Hill Country patio cover installs across Boerne and Kendall County." },
      ]}
      postCta={<SaSiblingCallouts current={PATH} />}
    />
  );
}

export function SaSiblingCallouts({ current }: { current: string }) {
  const others = SA_SIBLINGS.filter((s) => s.to !== current);
  return (
    <section className="border-t-4 border-navy-deep bg-white py-16">
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <div className="text-center">
          <div className="font-display text-sm tracking-[0.3em] text-red">★ MORE SAN ANTONIO SHADE SERVICES ★</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">All Our San Antonio Landing Pages</h2>
        </div>
        <ul className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {others.map((s) => (
            <li key={s.to} className="border-l-4 border-red bg-cream p-5">
              <Link to={s.to} className="font-display text-lg font-bold uppercase tracking-wide text-navy-deep hover:text-red">
                {s.label}
              </Link>
              <p className="mt-2 text-sm text-navy-deep/80">{s.blurb}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
